import React from 'react';
import { apiClient, ApiError } from '../../../lib/apiClient';
import { DMA_REGIONS } from '../../../data/dmaList';
import { transformFeature } from './usMapProjection';

// CTV-445: national DMA boundaries for the report's DMA choropleth. Fetched from
// the selfservice-api POST /geographic/dma-boundaries (ST_AsGeoJSON over
// census_dmas.boundary) — the DMA analogue of the ZIP heatmap's boundary fetch.
// We request every known DMA code so the map draws the full national base (all
// markets gray, delivered ones colored), then cache module-wide so toggling in
// and out of the DMA view doesn't re-fetch.
//
// CTV-689: a failed fetch used to collapse into a silent, permanent empty map.
// The hook now retries with backoff, reports its status, and exposes a manual
// retry so the consumers can render visible loading/error states instead.

interface DmaBoundary {
  dma_code: string;
  dma_name: string;
  boundary: GeoJSON.Geometry | null;
}

let cachedData: GeoJSON.FeatureCollection | null = null;
let inFlight: Promise<GeoJSON.FeatureCollection> | null = null;

async function fetchOnce(): Promise<GeoJSON.FeatureCollection> {
  if (cachedData) return cachedData;
  if (inFlight) return inFlight;
  const dmaCodes = DMA_REGIONS.map((d) => d.code);
  inFlight = apiClient
    .post<{ dmas: DmaBoundary[] }>('/geographic/dma-boundaries', { dmaCodes })
    .then((r) => {
      const features = (r.dmas ?? [])
        .filter((d) => d.boundary)
        .map((d) =>
          transformFeature({
            type: 'Feature',
            geometry: d.boundary as GeoJSON.Geometry,
            properties: { dma_code: d.dma_code, dma_name: d.dma_name },
          }),
        );
      cachedData = { type: 'FeatureCollection', features };
      return cachedData;
    });
  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

// Delays before attempts 2..4; attempt 1 fires immediately, so one epoch is
// four attempts total before the terminal 'error'.
const BACKOFF_MS = [1000, 2000, 4000];
// When the failure is a 429 the server is telling us to back off; retrying
// inside the same fixed window just burns budget, so floor the delay.
const RATE_LIMIT_MIN_DELAY_MS = 5000;

export type DmaBoundariesStatus = 'loading' | 'retrying' | 'error' | 'ready';

export interface DmaBoundariesResult {
  data: GeoJSON.FeatureCollection | null;
  status: DmaBoundariesStatus;
  /** Resets the backoff schedule and refetches immediately. */
  retry: () => void;
}

export function useDmaBoundaries(): DmaBoundariesResult {
  const [state, setState] = React.useState<{
    data: GeoJSON.FeatureCollection | null;
    status: DmaBoundariesStatus;
  }>(() => (cachedData ? { data: cachedData, status: 'ready' } : { data: null, status: 'loading' }));
  // Each epoch is one full backoff schedule; retry() starts a fresh one.
  const [attemptEpoch, setAttemptEpoch] = React.useState(0);

  React.useEffect(() => {
    if (cachedData) {
      setState({ data: cachedData, status: 'ready' });
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const attempt = (n: number) => {
      fetchOnce()
        .then((d) => {
          if (!cancelled) setState({ data: d, status: 'ready' });
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          if (n >= BACKOFF_MS.length) {
            setState({ data: null, status: 'error' });
            return;
          }
          const rateLimited = err instanceof ApiError && err.status === 429;
          const delay = rateLimited
            ? Math.max(BACKOFF_MS[n], RATE_LIMIT_MIN_DELAY_MS)
            : BACKOFF_MS[n];
          setState({ data: null, status: 'retrying' });
          timer = setTimeout(() => attempt(n + 1), delay);
        });
    };
    attempt(0);

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [attemptEpoch]);

  const retry = React.useCallback(() => {
    setState({ data: null, status: 'loading' });
    setAttemptEpoch((e) => e + 1);
  }, []);

  return { data: state.data, status: state.status, retry };
}
